import { configureStore } from "@reduxjs/toolkit";
import createSagaMiddleware from "redux-saga";
import createLogger from "redux-logger";
import { rootReducer } from "./reducers";
import { registerRequestSaga, unregisterRequestSaga, createConferenceSaga, joinConferenceSaga, } from "./sagas";
import { AuviousRtcState } from "./model";
import { mqttEvent } from "./actions";
import { taskManagerController } from "./call/sagas";
import { callCreateRequest } from "./call/actions";
import { isLocalStorageSupported } from "../Common";
import { eventLog } from "../log";
export class AuviousRtcRedux {
    constructor() {
        this.sagaMiddleWare = createSagaMiddleware();
        this.store = configureStore({
            reducer: rootReducer,
            middleware: (getDefaultMiddleware) => [
                ...getDefaultMiddleware(),
                this.sagaMiddleWare,
                ...(isLocalStorageSupported() &&
                    (localStorage === null || localStorage === void 0 ? void 0 : localStorage.getItem("auvious-redux-logger")) === "true"
                    ? [createLogger]
                    : []),
            ],
        });
        this.eventHandlers = {};
        this.callTasks = {};
        this.runSaga(taskManagerController, this);
    }
    // IAuviousApi methods start
    register(options) {
        return new Promise((resolve, reject) => {
            this.registerTask = this.runSaga(registerRequestSaga, {
                context: this,
                options,
                resolve,
                reject,
            });
        });
    }
    unregister() {
        return new Promise((resolve, reject) => {
            this.unregisterTask = this.runSaga(unregisterRequestSaga, {
                context: this,
                resolve,
                reject,
            });
        });
    }
    registered() {
        return this.store.getState().state === AuviousRtcState.REGISTERED;
    }
    identity() {
        return this.store.getState().identity;
    }
    endpoint() {
        return this.store.getState().endpoint;
    }
    call(target, options) {
        this.callTasks[target] = this.callTasks[target] || {};
        this.callTasks[target].options = options;
        this.store.dispatch(callCreateRequest({
            targetEndpoint: {
                username: target,
                endpoint: null,
            },
            dir: "outgoing",
        }));
    }
    createConference(options) {
        return new Promise((resolve, reject) => {
            this.runSaga(createConferenceSaga, {
                context: this,
                options,
                resolve,
                reject,
            });
        });
    }
    joinConference(options) {
        return new Promise((resolve, reject) => {
            this.runSaga(joinConferenceSaga, {
                context: this,
                options,
                resolve,
                reject,
            });
        });
    }
    message(username, message) {
        throw new Error("Method not implemented.");
    }
    on(event, handler) {
        this.setEventHandler(event, handler);
    }
    // IAuviousApi methods end.
    // public methods used in sagas start
    /**
     * Return existing call context or new empty one
     * @param username - the callee username
     */
    getCallContext(username) {
        this.callTasks[username] = this.callTasks[username] || { options: {} };
        return this.callTasks[username];
    }
    sendEvent(eventId, data) {
        eventLog.debug(`ua event ${eventId}`, data);
        if (this.hasEventHandler(eventId)) {
            try {
                if (data) {
                    this.getEventHandler(eventId)(data);
                }
                else {
                    this.getEventHandler(eventId)();
                }
            }
            catch (error) {
                eventLog.error(`error while executing ua ${eventId} handler`, error);
            }
        }
    }
    setCommonClient(commonClient) {
        this.commonClient = commonClient;
        if (this.eventsSubscription) {
            this.eventsSubscription.dispose();
        }
        let disconnected = false;
        let disconnectedCounter = 0;
        this.eventsSubscription = commonClient.events.setEventHandlers({
            onMessageReceived: (event) => this.store.dispatch(mqttEvent(event.message || event.payload)),
            onDisconnected: () => {
                disconnected = true;
                disconnectedCounter++;
                if (disconnectedCounter > 15) {
                    this.store.dispatch(mqttEvent({
                        type: "KeepAliveReminder",
                        timestamp: Date.now().toString(),
                        counter: 0,
                    }));
                }
            },
            onConnected: () => {
                if (disconnected) {
                    this.store.dispatch(mqttEvent({
                        type: "KeepAliveReminder",
                        timestamp: Date.now().toString(),
                        counter: 0,
                    }));
                    disconnected = false;
                    disconnectedCounter = 0;
                }
            },
        });
        this.userEndpointResource = commonClient.apiResourceFactory("rtc-api/users/endpoints");
        this.conferenceResource = commonClient.apiResourceFactory("rtc-api/conferences");
        this.callResource = commonClient.apiResourceFactory("rtc-api/calls");
        this.iceResource = commonClient.apiResourceFactory("rtc-api/");
        this.statsResource = commonClient.apiResourceFactory("rtc-stats/api/");
    }
    unsetCommonClient() {
        if (this.eventsSubscription) {
            this.eventsSubscription.dispose();
        }
        this.commonClient = null;
    }
    getCommonClient() {
        return this.commonClient;
    }
    getUserEndpointResource() {
        return this.userEndpointResource;
    }
    getConferenceResource() {
        return this.conferenceResource;
    }
    getCallResource() {
        return this.callResource;
    }
    getIceResource() {
        return this.iceResource;
    }
    getStatsResource() {
        return this.statsResource;
    }
    hasEventHandler(name) {
        return !!this.eventHandlers[name];
    }
    getEventHandler(name) {
        return this.eventHandlers[name];
    }
    setEventHandler(name, handler) {
        this.eventHandlers[name] = handler;
    }
    setEventHandlers(eventHandlers) {
        this.eventHandlers = eventHandlers;
    }
    getRegisterTask() {
        return this.registerTask;
    }
    getUnregisterTask() {
        return this.unregisterTask;
    }
    getEndpointTopic() {
        return `user/${this.identity()}/${this.endpoint()}`;
    }
    getStore() {
        return this.store;
    }
    runSaga(saga, ...args) {
        return this.sagaMiddleWare.run(saga, ...args);
    }
}
//# sourceMappingURL=AuviousRtcRedux.js.map